import React, { useState, useEffect } from 'react';
import { Plus, Search, Truck, FileText, CheckCircle, MoreVertical, Eye, MapPin, User, Phone, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  getDeliveryNotes, createDeliveryNote, issueDeliveryNote, 
  markDelivered, acknowledgeDelivery,
  getContracts, getCompanies 
} from '../lib/api';

const ASSET_TYPES = [
  'Pickup', 'Truck', 'Bus', 'Excavator', 'Bulldozer', 'Crane', 'Loader', 
  'Forklift', 'Trailer', 'Tanker', 'Compactor', 'Grader', 'Roller'
];

const emptyDN = {
  company_id: '',
  contract_id: '',
  items: [],
  delivery_date: '',
  delivery_time: '',
  delivery_location: '',
  driver_name: '',
  driver_phone: '',
  vehicle_number: '',
  notes: ''
};

const emptyItem = { description: '', asset_type: '', quantity: 1, registration_numbers: [], condition: 'good', notes: '' };

export const DeliveryNotesPage = () => {
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isAckDialogOpen, setIsAckDialogOpen] = useState(false);
  const [selectedDN, setSelectedDN] = useState(null);
  const [formData, setFormData] = useState(emptyDN);
  const [ackName, setAckName] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dnsRes, contractsRes, companiesRes] = await Promise.all([
        getDeliveryNotes(null, null, statusFilter !== 'all' ? statusFilter : null),
        getContracts(),
        getCompanies()
      ]);
      setDeliveryNotes(dnsRes.data);
      setContracts(contractsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error('Failed to load delivery notes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedDN(null);
    setFormData({ 
      ...emptyDN, 
      company_id: companies[0]?.id || '',
      delivery_date: new Date().toISOString().split('T')[0]
    });
    setIsDialogOpen(true);
  };

  const handleView = (dn) => {
    setSelectedDN(dn);
    setIsViewDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.company_id || !formData.contract_id || formData.items.length === 0 || !formData.delivery_location) {
      toast.error('Please fill in required fields and add at least one item');
      return;
    }

    try {
      await createDeliveryNote(formData);
      toast.success('Delivery Note created');
      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to create Delivery Note');
      console.error(error);
    }
  };

  const handleIssue = async (dn) => {
    try {
      await issueDeliveryNote(dn.id);
      toast.success('Delivery Note issued');
      loadData();
    } catch (error) {
      toast.error('Failed to issue Delivery Note');
    }
  };

  const handleDeliver = async (dn) => {
    try {
      await markDelivered(dn.id);
      toast.success('Marked as delivered');
      loadData();
    } catch (error) {
      toast.error('Failed to mark as delivered');
    }
  };

  const handleAckDialog = (dn) => {
    setSelectedDN(dn);
    setAckName('');
    setIsAckDialogOpen(true);
  };

  const handleAcknowledge = async () => {
    if (!ackName.trim()) {
      toast.error('Please enter the name of the person acknowledging');
      return;
    }
    try {
      await acknowledgeDelivery(selectedDN.id, ackName);
      toast.success('Delivery acknowledged');
      setIsAckDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to acknowledge delivery');
    }
  };

  // Item management
  const addItem = () => {
    setFormData({ ...formData, items: [...formData.items, { ...emptyItem }] });
  };
  const removeItem = (index) => {
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };
  const updateItem = (index, field, value) => {
    const updated = [...formData.items];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, items: updated });
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: 'bg-gray-100 text-gray-800',
      issued: 'bg-blue-100 text-blue-800',
      delivered: 'bg-yellow-100 text-yellow-800',
      acknowledged: 'bg-green-100 text-green-800'
    };
    return <Badge className={variants[status] || variants.draft}>{status}</Badge>;
  };

  const getContractInfo = (contractId) => {
    const contract = contracts.find(c => c.id === contractId);
    return contract ? `${contract.contract_number} - ${contract.client_name}` : 'Unknown';
  };

  const filteredDNs = deliveryNotes.filter(dn => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return dn.dn_number?.toLowerCase().includes(term) || 
           dn.client_name?.toLowerCase().includes(term);
  });

  return (
    <div className="space-y-6" data-testid="delivery-notes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Delivery Notes</h1>
          <p className="text-muted-foreground mt-1">Issue delivery notes for client mobilizations</p>
        </div>
        <Button onClick={handleCreate} data-testid="create-dn-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Delivery Note
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by DN number or client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{deliveryNotes.length}</p>
                <p className="text-sm text-muted-foreground">Total DNs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Truck className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {deliveryNotes.filter(dn => dn.status === 'issued').length}
                </p>
                <p className="text-sm text-muted-foreground">In Transit</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <MapPin className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {deliveryNotes.filter(dn => dn.status === 'delivered').length}
                </p>
                <p className="text-sm text-muted-foreground">Pending Ack</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {deliveryNotes.filter(dn => dn.status === 'acknowledged').length}
                </p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* DN List */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading delivery notes...</div>
      ) : filteredDNs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">No delivery notes found</h3>
            <p className="text-muted-foreground mt-1">Create your first delivery note to get started</p>
            <Button className="mt-4" onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Delivery Note
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredDNs.map((dn) => (
            <Card key={dn.id} className="hover:shadow-md transition-shadow" data-testid={`dn-card-${dn.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="font-semibold text-lg">{dn.dn_number}</p>
                      <p className="text-sm text-muted-foreground">{dn.client_name}</p>
                    </div>
                    {getStatusBadge(dn.status)}
                  </div>
                  
                  <div className="flex items-center gap-8">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      {new Date(dn.delivery_date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      {dn.delivery_location?.substring(0, 30)}...
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleView(dn)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        {dn.status === 'draft' && (
                          <DropdownMenuItem onClick={() => handleIssue(dn)}>
                            <FileText className="h-4 w-4 mr-2" />
                            Issue DN
                          </DropdownMenuItem>
                        )}
                        {dn.status === 'issued' && (
                          <DropdownMenuItem onClick={() => handleDeliver(dn)}>
                            <Truck className="h-4 w-4 mr-2" />
                            Mark Delivered
                          </DropdownMenuItem>
                        )}
                        {dn.status === 'delivered' && (
                          <DropdownMenuItem onClick={() => handleAckDialog(dn)}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Record Acknowledgment
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Delivery Note</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company *</Label>
                <Select value={formData.company_id} onValueChange={(v) => setFormData({ ...formData, company_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Contract *</Label>
                <Select value={formData.contract_id} onValueChange={(v) => setFormData({ ...formData, contract_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select contract" />
                  </SelectTrigger>
                  <SelectContent>
                    {contracts.filter(c => c.status === 'po_received' || c.status === 'active').map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.contract_number} - {c.client_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Delivery Date *</Label>
                <Input
                  type="date"
                  value={formData.delivery_date}
                  onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Delivery Time</Label>
                <Input
                  type="time"
                  value={formData.delivery_time}
                  onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Delivery Location *</Label>
                <Input
                  value={formData.delivery_location}
                  onChange={(e) => setFormData({ ...formData, delivery_location: e.target.value })}
                  placeholder="Full delivery address"
                />
              </div>
              <div className="space-y-2">
                <Label>Driver Name</Label>
                <Input
                  value={formData.driver_name}
                  onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                  placeholder="Driver name"
                />
              </div>
              <div className="space-y-2">
                <Label>Driver Phone</Label>
                <Input
                  value={formData.driver_phone}
                  onChange={(e) => setFormData({ ...formData, driver_phone: e.target.value })}
                  placeholder="+966..."
                />
              </div>
              <div className="space-y-2">
                <Label>Vehicle Number</Label>
                <Input
                  value={formData.vehicle_number}
                  onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
                  placeholder="Plate number"
                />
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <Label className="text-base font-semibold">Equipment Items *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              </div>

              {formData.items.length === 0 ? (
                <div className="text-center py-6 border rounded-lg text-muted-foreground">
                  No items added. Click &quot;Add Item&quot; to add equipment.
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <Card key={index}>
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-4 gap-3">
                          <Select 
                            value={item.asset_type} 
                            onValueChange={(v) => updateItem(index, 'asset_type', v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Asset Type *" />
                            </SelectTrigger>
                            <SelectContent>
                              {ASSET_TYPES.map((type) => (
                                <SelectItem key={type} value={type}>{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            placeholder="Quantity"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                          />
                          <Select 
                            value={item.condition} 
                            onValueChange={(v) => updateItem(index, 'condition', v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Condition" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="excellent">Excellent</SelectItem>
                              <SelectItem value="good">Good</SelectItem>
                              <SelectItem value="fair">Fair</SelectItem>
                              <SelectItem value="needs_repair">Needs Repair</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeItem(index)}
                            className="text-red-500"
                          >
                            ×
                          </Button>
                          <div className="col-span-4">
                            <Input
                              placeholder="Registration numbers (comma separated)"
                              value={item.registration_numbers?.join(', ') || ''}
                              onChange={(e) => updateItem(index, 'registration_numbers', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes or special instructions..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-dn-btn">Create Delivery Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Delivery Note Details</DialogTitle>
          </DialogHeader>
          
          {selectedDN && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold">{selectedDN.dn_number}</h3>
                  <p className="text-muted-foreground">{selectedDN.client_name}</p>
                </div>
                {getStatusBadge(selectedDN.status)}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Delivery Date</p>
                  <p className="font-medium">{new Date(selectedDN.delivery_date).toLocaleDateString()}</p>
                </div>
                {selectedDN.delivery_time && (
                  <div>
                    <p className="text-muted-foreground">Delivery Time</p>
                    <p className="font-medium">{selectedDN.delivery_time}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-muted-foreground">Delivery Location</p>
                  <p className="font-medium">{selectedDN.delivery_location}</p>
                </div>
                {selectedDN.driver_name && (
                  <div>
                    <p className="text-muted-foreground">Driver</p>
                    <p className="font-medium">{selectedDN.driver_name}</p>
                  </div>
                )}
                {selectedDN.driver_phone && (
                  <div>
                    <p className="text-muted-foreground">Driver Phone</p>
                    <p className="font-medium">{selectedDN.driver_phone}</p>
                  </div>
                )}
                {selectedDN.vehicle_number && (
                  <div>
                    <p className="text-muted-foreground">Vehicle</p>
                    <p className="font-medium">{selectedDN.vehicle_number}</p>
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-semibold mb-2">Items</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Type</th>
                      <th className="text-center py-2">Qty</th>
                      <th className="text-center py-2">Condition</th>
                      <th className="text-left py-2">Reg Numbers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDN.items?.map((item, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-2">{item.asset_type}</td>
                        <td className="text-center py-2">{item.quantity}</td>
                        <td className="text-center py-2">{item.condition}</td>
                        <td className="py-2">{item.registration_numbers?.join(', ') || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedDN.client_acknowledged_by && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-1">Acknowledged</h4>
                  <p className="text-sm text-green-700">
                    By: {selectedDN.client_acknowledged_by} on {new Date(selectedDN.acknowledged_at).toLocaleString()}
                  </p>
                </div>
              )}

              {selectedDN.notes && (
                <div>
                  <h4 className="font-semibold mb-1">Notes</h4>
                  <p className="text-sm text-muted-foreground">{selectedDN.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Acknowledgment Dialog */}
      <Dialog open={isAckDialogOpen} onOpenChange={setIsAckDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Client Acknowledgment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Enter the name of the client representative who received the delivery.
            </p>
            <div className="space-y-2">
              <Label>Acknowledged By *</Label>
              <Input
                value={ackName}
                onChange={(e) => setAckName(e.target.value)}
                placeholder="Client representative name"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAckDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAcknowledge}>Confirm Acknowledgment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
